import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/feature/Pagination";
import { categories } from "@/mocks/categories";
import { movies } from "@/mocks/movies";
import usePageMeta from "@/hooks/usePageMeta";
import FilterPanel, { type FilterState } from "./components/FilterPanel";

const PAGE_SIZE = 8;

const emptyFilters: FilterState = {
  region: "all",
  year: "all",
  length: "all",
  tags: [],
};

const sortOptions = [
  { value: "newest", label: "最新发布" },
  { value: "year", label: "年份（新 → 旧）" },
  { value: "rating", label: "评分最高" },
  { value: "title", label: "标题排序" },
];

const matchYear = (year: number, range: string) => {
  if (range === "2020s") return year >= 2020;
  if (range === "2010s-late") return year >= 2015 && year <= 2019;
  if (range === "2010s-early") return year >= 2010 && year <= 2014;
  if (range === "before-2010") return year < 2010;
  return true;
};

export default function CategoryPage() {
  const { type = "" } = useParams();
  const category = categories.find((item) => item.type === type);
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const activeCount =
    (filters.region !== "all" ? 1 : 0) +
    (filters.year !== "all" ? 1 : 0) +
    (filters.length !== "all" ? 1 : 0) +
    filters.tags.length;

  usePageMeta(
    category
      ? `【${category.name}】- 日韩黄色电影资源分类`
      : "分类不存在 - 日韩黄色电影",
    category
      ? `${category.description} 日韩黄色电影【${category.name}】分类页收录 ${movies.filter((movie) => movie.collections.includes(category.type)).length} 部日韩情色电影，可按地区、年份、片长与题材筛选。`
      : "你访问的分类不存在，请返回首页重新选择日韩影片分类。",
    category
      ? `${category.name},日韩黄色电影,日韩情色电影,${category.nameEn},影片分类`
      : "日韩黄色电影,影片分类",
  );

  const baseList = useMemo(
    () => (category ? movies.filter((movie) => movie.collections.includes(category.type)) : []),
    [category],
  );

  const filtered = useMemo(() => {
    const result = baseList.filter((movie) => {
      if (filters.region !== "all" && movie.region !== filters.region) return false;
      if (!matchYear(movie.year, filters.year)) return false;
      if (filters.length !== "all" && movie.lengthType !== filters.length) return false;
      if (filters.tags.length > 0 && !filters.tags.some((tag) => movie.genres.includes(tag))) {
        return false;
      }
      return true;
    });

    const sorted = [...result];
    if (sortBy === "year") {
      sorted.sort((a, b) => b.year - a.year);
    } else if (sortBy === "rating") {
      sorted.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === "title") {
      sorted.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
    } else {
      sorted.sort((a, b) => b.publishDate.localeCompare(a.publishDate));
    }
    return sorted;
  }, [baseList, filters, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const resetAll = () => {
    setFilters(emptyFilters);
    setSortBy("newest");
    setPage(1);
  };

  if (!category) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 md:py-32">
          <div className="w-full max-w-[560px] mx-auto text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-100 border border-background-300/70 flex items-center justify-center text-2xl text-foreground-500">
              <i className="ri-folder-3-line" />
            </span>
            <h1 className="mt-6 font-heading text-2xl text-foreground-950">该分类不存在</h1>
            <p className="mt-3 text-sm text-foreground-500 leading-relaxed">
              你访问的分类可能已被调整或尚未建立，请从下方入口重新选择。
            </p>
            <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/category/popular"
                className="h-11 px-6 rounded-md bg-primary-500 text-foreground-950 text-sm inline-flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                前往热门排行
              </Link>
              <Link
                to="/"
                className="h-11 px-6 rounded-md border border-background-400/60 text-foreground-800 text-sm inline-flex items-center justify-center cursor-pointer hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-8 md:pt-10 pb-14 md:pb-20 bg-background-50">
        <div className="w-full max-w-[1400px] mx-auto">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] md:text-xs text-foreground-500">
            <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400">影片分类</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-500">{category.name}</span>
          </nav>

          <div className="mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-md bg-primary-500/10 text-primary-700 flex items-center justify-center text-xl">
                  <i className={category.icon} />
                </span>
                <div>
                  <h1 className="font-heading text-2xl md:text-4xl text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="mt-1 text-[10px] tracking-[0.24em] uppercase text-foreground-400">
                    {category.nameEn}
                  </p>
                </div>
              </div>
              <p className="mt-4 max-w-[680px] text-xs md:text-sm leading-relaxed text-foreground-500">
                {category.description}
              </p>
            </div>

            <div className="flex items-center justify-between lg:justify-end gap-3 shrink-0">
              <span className="text-xs text-foreground-500 whitespace-nowrap">
                共 <span className="text-accent-500">{filtered.length}</span> 部作品
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen((prev) => !prev)}
                  className="lg:hidden h-9 px-3 rounded-md border border-background-300/70 text-xs text-foreground-700 flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line" />
                  筛选
                  {activeCount > 0 && (
                    <span className="px-1.5 rounded-full bg-primary-500 text-[10px] text-foreground-950">
                      {activeCount}
                    </span>
                  )}
                </button>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(event) => {
                      setSortBy(event.target.value);
                      setPage(1);
                    }}
                    className="h-9 pl-3 pr-8 rounded-md bg-background-100 border border-background-300/70 text-xs text-foreground-700 outline-none focus:border-primary-500 cursor-pointer appearance-none"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value} className="bg-background-100">
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-[248px_1fr] gap-6 lg:gap-8">
            <aside className="w-full lg:sticky lg:top-24 self-start">
              <div className={mobileFilterOpen ? "block" : "hidden lg:block"}>
                <FilterPanel
                  value={filters}
                  onChange={updateFilters}
                  onReset={resetAll}
                  activeCount={activeCount}
                />
              </div>
            </aside>

            <div className="min-w-0">
              {paged.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-5">
                    {paged.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                  <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
                </>
              ) : (
                <div className="w-full rounded-lg border border-background-300/60 bg-background-100 px-6 py-16 text-center">
                  <span className="w-14 h-14 mx-auto rounded-full bg-background-200/70 flex items-center justify-center text-xl text-foreground-500">
                    <i className="ri-search-eye-line" />
                  </span>
                  <h2 className="mt-5 text-base text-foreground-900">暂无符合条件的影片</h2>
                  <p className="mt-2 text-xs md:text-sm text-foreground-500">您可以重置筛选条件后重新浏览。</p>
                  <button
                    type="button"
                    onClick={resetAll}
                    className="mt-6 h-10 px-5 rounded-md bg-accent-500 text-background-50 text-xs font-label tracking-widest uppercase cursor-pointer hover:bg-accent-600 transition-colors whitespace-nowrap"
                  >
                    重置筛选
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}